import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import {
  DetailCloseButton,
  DetailSidebarFallback,
  SectionHeading,
  StatusBadge,
} from "@/accounts-payable/common";
import { useGetEmail, useGetInvoicesByIds } from "@/accounts-payable/query";
import { useReceiptsEnabled } from "@/accounts-payable/use-receipts-enabled";
import { EMAIL_STATUS, formatApDateTime } from "@/accounts-payable/utils";
import { EmailAttachmentRow } from "./email-attachment-row";

/**
 * Email detail rendered inside the shared SimpleSidebar (not a bespoke drawer).
 * Fetches the email by id, so deep links resolve regardless of which pages the
 * paginated list has loaded.
 */
export const ApEmailDetail = ({
  emailId,
  businessId,
  onClose,
}: {
  emailId: string;
  businessId: string | null;
  onClose: () => void;
}) => {
  const { data: email, isPending } = useGetEmail(businessId, emailId);
  const receiptsEnabled = useReceiptsEnabled();
  const { invoices, isLoading: invoicesLoading } = useGetInvoicesByIds(
    businessId,
    email?.attachments.flatMap((a) => a.invoice_ids) ?? [],
  );

  if (!email) {
    return (
      <DetailSidebarFallback
        loading={isPending}
        errorMessage="Couldn’t load the email. Please try again."
        onClose={onClose}
      />
    );
  }

  return (
    <Stack gap="none" fullWidth className="h-full">
      {/* Header */}
      <Stack
        gap="sm"
        fullWidth
        className="flex-none border-b border-stroke-tertiary bg-bg-secondary/50 p-6"
      >
        <Stack
          direction="row"
          justify="between"
          align="start"
          gap="md"
          fullWidth
        >
          <Stack direction="row" gap="sm" align="center" className="flex-wrap">
            <StatusBadge meta={EMAIL_STATUS[email.status]} />
            <Typography textColor="tertiary">
              {formatApDateTime(email.received_at)}
            </Typography>
          </Stack>
          <DetailCloseButton onClose={onClose} />
        </Stack>
        <Typography variant="subtitleW1">{email.subject}</Typography>
        <Typography textColor="tertiary">From {email.from}</Typography>
      </Stack>

      {/* Body */}
      <ScrollArea className="min-h-0 w-full flex-1">
        {/* Attachments lead: they hold the outcome and the one action (open
            the invoice). The message is read-only background, so it sits
            below on a quiet grey surface. */}
        <Stack gap="lg" fullWidth className="px-6 py-6">
          <Stack gap="sm" fullWidth>
            <SectionHeading>
              Attachments ({email.attachments.length})
            </SectionHeading>
            {/* Plain rows, no cards inside the panel; the file icons mark
                where each attachment starts. */}
            <Stack gap="lg" fullWidth>
              {email.attachments.map((attachment) => (
                <EmailAttachmentRow
                  key={attachment.id}
                  attachment={attachment}
                  invoices={invoices}
                  receiptsEnabled={receiptsEnabled}
                  invoicesLoading={invoicesLoading}
                />
              ))}
            </Stack>
          </Stack>

          {email.message && (
            <Stack gap="sm" fullWidth>
              <SectionHeading>Message</SectionHeading>
              <Stack
                gap="none"
                fullWidth
                className="rounded-lg bg-surface-secondary p-4"
              >
                <Typography
                  textColor="secondary"
                  className="whitespace-pre-wrap leading-relaxed"
                >
                  {email.message}
                </Typography>
              </Stack>
            </Stack>
          )}
        </Stack>
      </ScrollArea>
    </Stack>
  );
};
